// =============================================================================
// SIREPPAR - Resumen de Documentación (pestaña dentro de /documentos)
// =============================================================================
// Tabla tipo "hoja impresa": una fila por partido con expediente de
// reempadronamiento en curso. Columnas = documentos obligatorios + Intimación/Obs.
// Al elegir un partido, la vista se corre a la pestaña "Documentos" con ese
// expediente cargado (vía onSelect).

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { partidoService } from '../../services/api';
import { useQuery } from '@tanstack/react-query';
import {
  DOCS_OBLIGATORIOS_BASE,
  DOCS_OBLIGATORIOS_EXTRA,
  DOCS_OBLIGATORIOS_TODOS,
  tipoDocLabels,
} from '../../utils/tiposDocumento';

// =============================================================================
// Tipos del endpoint /partidos/estado-documentacion
// =============================================================================

interface DocResumen {
  tipo: string;
  dniPersona: string | null;
  estadoAdmisibilidad: 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';
  estadoAnalisis: 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';
  observacionAdmisibilidad: string | null;
  observacionAnalisis: string | null;
}

interface ObsResumen {
  id: string;
  tipo: string;      // 'OBSERVACION' | 'INTIMACION'
  estado: string;
  subsanada: boolean;
  esBorrador: boolean;
  respondida: boolean;
  creadoEn: string;
}

interface PartidoResumen {
  partidoId: string;
  numero: number;
  denominacion: string;
  sigla: string | null;
  tipoPartido: string;
  // Clase de agrupación (ESTANDAR / CONFEDERACION / ALIANZA). Separada del ámbito desde
  // que CONFEDERACION dejó de ser un `TipoPartido`.
  clasePartido: string;
  expedienteId: string;
  expedienteNumero: string;
  estadoExpediente: string;
  documentos: DocResumen[];
  observaciones: ObsResumen[];
}

interface Props {
  // Si está presente, al elegir un partido se notifica para correr la vista a la
  // pestaña "Documentos" con ese expediente cargado (en vez de navegar).
  onSelect?: (expedienteId: string, partidoId: string) => void;
}

// =============================================================================
// Constantes de presentación
// =============================================================================

type EstadoCelda = 'verificado' | 'rechazado' | 'pendiente' | 'faltante' | 'na';

const LABEL_CORTO: Record<string, string> = {
  RESOLUCION_RECONOCIMIENTO: 'Reconocimient.',
  CARTA_ORGANICA: 'Carta Orgánica',
  BASES_ACCION_POLITICA: 'Bases Acción Pol.',
  ACTA_ASAMBLEA: 'Acta Asamblea',
  PUBLICACION_LLAMADO_ELECCIONES: 'Publ. Llamado Elec.',
  CRONOGRAMA_ELECCIONES: 'Cronograma Elec.',
  CERTIFICADO_AUTORIDADES: 'Res. Autoridades',
  ACTA_DESIGNACION_APODERADOS: 'Acta Apoderados',
  CONSTANCIA_CUENTA_BANCARIA: 'Cta. Bancaria',
  PADRON_AFILIADOS: 'Padrón Afiliados',
  PADRON_AFILIADOS_PDF: 'Padrón Certificado',
  RESOLUCION_JUSTICIA_ELECTORAL: 'Res. Justicia Elect.',
  CONSTANCIA_VIGENCIA_JUZGADO: 'Const. Vigencia Juzgado',
  ACUERDO_CONSTITUTIVO: 'Acuerdo Constitutivo',
  ACUERDO_FUNCIONAMIENTO: 'Acuerdo Funcionam.',
  CONSTANCIA_VIGENCIA_PARTIDOS: 'Vigencia Partidos',
  CONFORMIDAD_PARTIDOS: 'Conformidad Partidos',
};

const COL_DISTRITO = new Set(DOCS_OBLIGATORIOS_EXTRA.DISTRITO);
const COL_CONFED = new Set(DOCS_OBLIGATORIOS_EXTRA.CONFEDERACION);

// Etapas y colores alineados con /gestion (GestionExpedientesPage).
const ETAPA_LABEL: Record<string, string> = {
  ADMISIBILIDAD: 'Admisibilidad',
  ANALISIS: 'Análisis',
  RESOLUCION: 'Resolución',
  FINALIZADO: 'Finalizado',
};

const tipoPartidoLabel: Record<string, string> = {
  MUNICIPAL: 'Municipal', DISTRITO: 'Distrital', PROVINCIAL: 'Provincial',
  NACIONAL: 'Nacional', CONFEDERACION: 'Confederación',
};

// Los cuatro primeros son ámbitos territoriales (`tipoPartido`); CONFEDERACION es una
// clase de agrupación (`clasePartido`). El filtro pregunta una sola cosa al usuario y
// resuelve contra la columna que corresponda — ver coincideFiltroTipo.
const TIPOS_PARTIDO_FILTRO = ['MUNICIPAL', 'PROVINCIAL', 'DISTRITO', 'NACIONAL', 'CONFEDERACION'];

function coincideFiltroTipo(p: { tipoPartido: string; clasePartido?: string }, filtro: string): boolean {
  if (!filtro) return true;
  if (filtro === 'CONFEDERACION') return p.clasePartido === 'CONFEDERACION';
  return p.tipoPartido === filtro;
}

// =============================================================================
// Lógica de proyección por etapa
// =============================================================================

function etapaDeExpediente(estado: string): keyof typeof ETAPA_LABEL {
  switch (estado) {
    case 'EN_ANALISIS':
    case 'OBSERVADO':
      return 'ANALISIS';
    case 'EN_SECRETARIO':
    case 'PENDIENTE_RESOLUCION':
      return 'RESOLUCION';
    case 'APROBADO':
      return 'FINALIZADO';
    default: // PRESENTADO, EN_ADMISIBILIDAD, INTIMADO
      return 'ADMISIBILIDAD';
  }
}

function enAnalisis(estado: string): boolean {
  return ['EN_ANALISIS', 'OBSERVADO', 'EN_SECRETARIO', 'PENDIENTE_RESOLUCION', 'APROBADO'].includes(estado);
}

function estadoDoc(d: DocResumen, analisis: boolean) {
  return analisis ? d.estadoAnalisis : d.estadoAdmisibilidad;
}
function motivoDoc(d: DocResumen, analisis: boolean) {
  return analisis ? d.observacionAnalisis : d.observacionAdmisibilidad;
}

/**
 * Documentos que siguen EN JUEGO en la etapa que se está mirando.
 *
 * En análisis quedan afuera los que se rechazaron en admisibilidad y nunca
 * llegaron a análisis: ese archivo ya se resolvió en la etapa anterior y su
 * dimensión de análisis quedó en PENDIENTE porque nadie la va a tocar nunca.
 * Contarlo hacía que un tipo con TODOS sus archivos verificados apareciera
 * "Pend." (caso real: el Acta de Asamblea de R1ET, con 3 archivos aprobados y
 * uno rechazado en admisibilidad). Es el mismo criterio que ya aplican
 * ExpedienteDetailPage y el gate del backend.
 */
function docsEnJuego(docs: DocResumen[], analisis: boolean): DocResumen[] {
  if (!analisis) return docs;
  return docs.filter(d => !(d.estadoAdmisibilidad === 'RECHAZADO' && d.estadoAnalisis !== 'RECHAZADO'));
}

function agregarEstado(todos: DocResumen[], analisis: boolean): EstadoCelda {
  if (todos.length === 0) return 'faltante';
  const docs = docsEnJuego(todos, analisis);
  // Si TODOS quedaron fuera de juego, el tipo no tiene nada vigente que mostrar:
  // lo que hay se rechazó en admisibilidad y no se repuso.
  if (docs.length === 0) return 'rechazado';
  // Un PENDIENTE manda: el tipo no figura "verificado" si hay algún documento sin
  // revisar (p. ej. uno nuevo recién subido). Queda "pendiente" hasta resolver todos.
  if (docs.some(d => estadoDoc(d, analisis) === 'PENDIENTE')) return 'pendiente';
  if (docs.some(d => estadoDoc(d, analisis) === 'APROBADO')) return 'verificado';
  return 'rechazado';
}

interface Celda { estado: EstadoCelda; motivo?: string; }

interface FilaResumen {
  p: PartidoResumen;
  etapa: keyof typeof ETAPA_LABEL;
  celdas: Record<string, Celda>;
  obs: { tipo: string; respondida: boolean } | null;   // intimación/observación vigente
  pendientes: number;
  todoVerificado: boolean;
}

function construirFila(p: PartidoResumen): FilaResumen {
  const analisis = enAnalisis(p.estadoExpediente);
  const etapa = etapaDeExpediente(p.estadoExpediente);

  // Espeja obligatoriosPorTipoPartido() del backend: las extras del ámbito y, si la
  // agrupación es una confederación, además las cuatro del art. 8.
  const obligatorios = new Set([
    ...DOCS_OBLIGATORIOS_BASE,
    ...(DOCS_OBLIGATORIOS_EXTRA[p.tipoPartido] || []),
    ...(p.clasePartido && p.clasePartido !== 'ESTANDAR' ? DOCS_OBLIGATORIOS_EXTRA[p.clasePartido] || [] : []),
  ]);

  const docsPorTipo: Record<string, DocResumen[]> = {};
  p.documentos.forEach(d => { (docsPorTipo[d.tipo] ||= []).push(d); });

  const celdas: Record<string, Celda> = {};
  let pendientes = 0;
  DOCS_OBLIGATORIOS_TODOS.forEach(tipo => {
    if (!obligatorios.has(tipo)) { celdas[tipo] = { estado: 'na' }; return; }
    const docs = docsPorTipo[tipo] || [];
    const estado = agregarEstado(docs, analisis);
    let motivo: string | undefined;
    if (estado === 'rechazado') {
      const enJuego = docsEnJuego(docs, analisis);
      const rechazado = (enJuego.length ? enJuego : docs).find(d => estadoDoc(d, analisis) === 'RECHAZADO')
        ?? docs.find(d => d.estadoAdmisibilidad === 'RECHAZADO');
      motivo = (rechazado && (motivoDoc(rechazado, analisis) || rechazado.observacionAdmisibilidad)) || undefined;
    }
    celdas[tipo] = { estado, motivo };
    if (estado !== 'verificado') pendientes++;
  });

  // Intimación/observación vigente: priorizo una sin responder; si no hay, la última.
  const obsReales = p.observaciones.filter(o => !o.esBorrador);
  const sinResponder = obsReales.filter(o => !o.respondida).sort((a, b) => b.creadoEn.localeCompare(a.creadoEn))[0];
  const ultima = [...obsReales].sort((a, b) => b.creadoEn.localeCompare(a.creadoEn))[0];
  const obsElegida = sinResponder || ultima;
  const obs = obsElegida ? { tipo: obsElegida.tipo, respondida: obsElegida.respondida } : null;

  const todoVerificado = DOCS_OBLIGATORIOS_TODOS.every(
    t => celdas[t].estado === 'verificado' || celdas[t].estado === 'na',
  );

  return { p, etapa, celdas, obs, pendientes, todoVerificado };
}

// =============================================================================
// Celda de documento (texto abreviado)
// =============================================================================

function CeldaDoc({ celda }: { celda: Celda }) {
  const base = 'inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold mx-auto whitespace-nowrap';
  if (celda.estado === 'na') {
    return <td className="text-center text-gray-300 font-semibold text-xs bg-gray-50/60">N/A</td>;
  }
  if (celda.estado === 'verificado') {
    return <td className="text-center px-1"><span className={`${base} bg-green-100 text-green-700`}>Verif.</span></td>;
  }
  if (celda.estado === 'rechazado') {
    return (
      <td className="text-center px-1" title={celda.motivo ? `Rechazado: ${celda.motivo}` : 'Rechazado'}>
        <span className={`${base} bg-red-100 text-red-700 cursor-help`}>Rech.</span>
      </td>
    );
  }
  if (celda.estado === 'pendiente') {
    return <td className="text-center px-1"><span className={`${base} bg-amber-100 text-amber-700`}>Pend.</span></td>;
  }
  // faltante
  return (
    <td className="text-center px-1" title="No presentado">
      <span className={`${base} bg-gray-100 text-gray-400`}>Falta</span>
    </td>
  );
}

// =============================================================================
// Componente principal
// =============================================================================

export default function ResumenDocumentacionTab({ onSelect }: Props) {
  const [tipoFilter, setTipoFilter] = useState('');
  const [etapaFilter, setEtapaFilter] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['resumen-documentacion'],
    queryFn: async () => {
      const r = await partidoService.getEstadoDocumentacion();
      return (r.data?.data ?? r.data) as PartidoResumen[];
    },
  });

  // Columnas visibles según el filtro de tipo: las de confederación solo aparecen
  // al filtrar por Confederación; las distritales con "todos" o "Distrital".
  const showDistrito = tipoFilter === '' || tipoFilter === 'DISTRITO';
  const showConfed = tipoFilter === 'CONFEDERACION';
  const columnas = useMemo(() => [
    ...DOCS_OBLIGATORIOS_BASE,
    ...(showDistrito ? DOCS_OBLIGATORIOS_EXTRA.DISTRITO : []),
    ...(showConfed ? DOCS_OBLIGATORIOS_EXTRA.CONFEDERACION : []),
  ], [showDistrito, showConfed]);

  const filas = useMemo(() => {
    if (!data) return [];
    return data
      .filter(p => coincideFiltroTipo(p, tipoFilter))
      .map(construirFila)
      .filter(f => !etapaFilter || f.etapa === etapaFilter)
      .sort((a, b) => {
        if (a.todoVerificado !== b.todoVerificado) return a.todoVerificado ? 1 : -1;
        return b.pendientes - a.pendientes;
      });
  }, [data, tipoFilter, etapaFilter]);

  if (error) {
    return <div className="bg-white rounded-lg shadow p-8 text-center text-red-600">Error al cargar el resumen de documentación.</div>;
  }

  return (
    <div className="space-y-4 resumen-print-root">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .resumen-print-area, .resumen-print-area * { visibility: visible !important; }
          .resumen-print-area { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
          .resumen-print-area .resumen-scroll { overflow: visible !important; }
          .resumen-no-print { display: none !important; }
          @page { size: landscape; margin: 8mm; }
        }
      `}</style>

      {/* Filtros + imprimir */}
      <div className="flex flex-wrap items-end gap-3 resumen-no-print">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tipo de partido</label>
          <select
            value={tipoFilter}
            onChange={e => setTipoFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px]"
          >
            <option value="">Todos los tipos</option>
            {TIPOS_PARTIDO_FILTRO.map(t => <option key={t} value={t}>{tipoPartidoLabel[t]}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Etapa</label>
          <select
            value={etapaFilter}
            onChange={e => setEtapaFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[160px]"
          >
            <option value="">Todas las etapas</option>
            {Object.entries(ETAPA_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {(tipoFilter || etapaFilter) && (
          <button onClick={() => { setTipoFilter(''); setEtapaFilter(''); }} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline">
            Limpiar
          </button>
        )}
        <div className="ml-auto">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            <PrinterIcon className="h-5 w-5" />
            Imprimir
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow resumen-print-area">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : filas.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay expedientes de reempadronamiento en curso con esos filtros.</div>
        ) : (
          <div className="overflow-x-auto resumen-scroll">
            <table className="w-full table-fixed text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-500">
                  <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider align-bottom w-[150px]">Partido</th>
                  <th className="px-1 py-2 text-left text-xs font-semibold uppercase tracking-wider align-bottom w-[98px]">Etapa</th>
                  {columnas.map(tipo => {
                    const shade = COL_DISTRITO.has(tipo) ? 'bg-indigo-50/40' : COL_CONFED.has(tipo) ? 'bg-purple-50/40' : '';
                    const txt = COL_DISTRITO.has(tipo) ? 'text-indigo-700' : COL_CONFED.has(tipo) ? 'text-purple-700' : 'text-gray-600';
                    return (
                      <th key={tipo} className={`px-1 py-2 align-bottom ${shade}`} title={tipoDocLabels[tipo] || tipo}>
                        <div className={`text-xs font-semibold leading-tight text-center ${txt}`}>{LABEL_CORTO[tipo] || tipoDocLabels[tipo] || tipo}</div>
                      </th>
                    );
                  })}
                  <th className="px-1 py-2 text-center text-xs font-semibold uppercase align-bottom border-l border-gray-200 w-[80px]">Intimad. / Observ.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filas.map(f => {
                  const verde = f.todoVerificado;
                  const irADocumentos = () => onSelect?.(f.p.expedienteId, f.p.partidoId);
                  return (
                    <tr key={f.p.partidoId} className={verde ? 'bg-green-50 hover:bg-green-100/70' : 'hover:bg-gray-50/70'}>
                      <td className={`sticky left-0 z-10 px-3 py-2.5 ${verde ? 'bg-green-50' : 'bg-white'}`}>
                        {onSelect ? (
                          <button onClick={irADocumentos} className="font-semibold text-blue-600 hover:text-blue-800 block leading-tight text-left">
                            {f.p.denominacion}
                          </button>
                        ) : (
                          <Link to={`/partidos/${f.p.partidoId}`} className="font-semibold text-blue-600 hover:text-blue-800 block leading-tight">
                            {f.p.denominacion}
                          </Link>
                        )}
                        <span className="text-xs text-gray-500">
                          {f.p.sigla ? `${f.p.sigla} · ` : ''}{tipoPartidoLabel[f.p.tipoPartido] || f.p.tipoPartido} · N° {f.p.numero}
                        </span>
                      </td>
                      <td className="px-1 py-2.5">
                        {onSelect ? (
                          <button onClick={irADocumentos} className="text-sm font-medium text-gray-700 hover:text-blue-600 hover:underline whitespace-nowrap" title={`Ver documentos · Exp. N° ${f.p.expedienteNumero}`}>
                            {ETAPA_LABEL[f.etapa]} ↗
                          </button>
                        ) : (
                          <Link to={`/expedientes/${f.p.expedienteId}`} className="text-sm font-medium text-gray-700 hover:text-blue-600 hover:underline whitespace-nowrap" title={`Expediente N° ${f.p.expedienteNumero}`}>
                            {ETAPA_LABEL[f.etapa]} ↗
                          </Link>
                        )}
                      </td>
                      {columnas.map(tipo => <CeldaDoc key={tipo} celda={f.celdas[tipo]} />)}
                      <td className="text-center border-l border-gray-200 px-1">
                        {f.obs ? (
                          <Link to={`/expedientes/${f.p.expedienteId}`} className="hover:underline" title={f.obs.respondida ? 'Con respuesta del partido' : 'Sin respuesta del partido'}>
                            <span className="text-xs font-medium text-gray-700 whitespace-nowrap">{f.obs.tipo === 'INTIMACION' ? 'Intimado' : 'Observado'} ↗</span>
                            <div className={`text-[10px] ${f.obs.respondida ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                              {f.obs.respondida ? '✓ resp.' : 'sin resp.'}
                            </div>
                          </Link>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
